<script setup lang="ts">
import { copyToClipboard } from '@teemill/modules/utils';
import highlighter from 'highlight.js';
import { uniqueId } from 'lodash';
import { onMounted, ref } from 'vue';
import { buildRichLinks } from './extensions/richTextRichLink';

const id = `tml-rich-text-${uniqueId()}`;
const containerRef = ref<HTMLElement>();

const props = defineProps<{
  transformLinks?: (url: URL) => URL | undefined;
}>();

onMounted(() => {
  if (!containerRef.value) {
    return;
  }

  document.querySelectorAll(`#${id} pre code`).forEach((element) => {
    highlighter.highlightElement(element as HTMLElement);

    const pre = element.parentElement;
    if (pre) {
      const parent = pre.parentElement;
      if (!parent) {
        return;
      }

      const wrapper = document.createElement('div');
      wrapper.className = 'code-block-wrapper';
      wrapper.style.position = 'relative';

      parent.insertBefore(wrapper, pre);
      wrapper.appendChild(pre);

      const copyButton = document.createElement('button');
      const language =
        Array.from(element.classList)
          .find((className) => className.startsWith('language-'))
          ?.replace('language-', '')
          ?.toLowerCase() || 'Copy';

      copyButton.innerHTML = language;
      copyButton.className = 'copiable';
      copyButton.onmouseenter = () => {
        copyButton.innerHTML = 'Copy';
      };
      copyButton.onmouseleave = () => {
        copyButton.innerHTML = language;
      };
      copyButton.onclick = () => {
        copyToClipboard(element.textContent || '');
      };
      wrapper.appendChild(copyButton);
    }
  });

  if (props.transformLinks) {
    document.querySelectorAll(`#${id} a`).forEach((element) => {
      if (element instanceof HTMLAnchorElement) {
        try {
          const url = props.transformLinks?.(new URL(element.href));

          if (url) {
            element.href = url.toString();
          }
        } catch (error) {
          console.warn(`Invalid URL in href attribute: ${element.href}`, error);
        }
      }
    });
  }

  buildRichLinks(containerRef.value);
});
</script>

<template>
  <div :id ref="containerRef" class="tml-rich-text">
    <slot />
  </div>
</template>

<style>
.tml-rich-text a {
  @apply text-link;
}

.tml-rich-text ul {
  @apply list-disc pl-4;
}
.tml-rich-text p,
.tml-rich-text h1,
.tml-rich-text h2,
.tml-rich-text h3,
.tml-rich-text h4,
.tml-rich-text h5,
.tml-rich-text h6,
.tml-rich-text blockquote {
  @apply my-4 first:mt-0 last:mb-0;
}

.tml-rich-text p {
  @apply min-h-6;
}

.tml-rich-text code:not(pre code) {
  @apply bg-[--tml-code-inline-background-color] text-[--tml-code-inline-text-color];
  @apply rounded-[--tml-code-inline-border-radius] px-[--tml-code-inline-padding-x] py-[--tml-code-inline-padding-y];
}

.tml-rich-text blockquote {
  @apply border-l-[0.25rem] border-[--tml-card-border-color];
  @apply italic pl-2 py-1;
}
.tml-rich-text blockquote > *:first-of-type {
  @apply m-0;
  @apply before:content-[open-quote];
  @apply after:content-[close-quote];
}

.tml-rich-text picture:last-child img {
  @apply pb-5;
}

.tml-rich-text pre {
  @apply relative;
}

.tml-rich-text .code-block-wrapper {
  @apply relative;
}

.tml-rich-text .copiable {
  @apply absolute right-2 top-2;
  @apply px-[--tml-code-block-copy-padding-x] py-[--tml-code-block-copy-padding-y];
  @apply text-xs;
  @apply bg-[--tml-code-block-copy-background-color] text-[--tml-code-block-copy-text-color];
  @apply rounded-[--tml-code-block-copy-border-radius];
  @apply shadow-[--tml-code-block-copy-shadow];
  @apply opacity-70 hover:opacity-100;
}

.tml-rich-text .table-wrapper {
  max-width: 100%;
  overflow-x: auto;
}

.tml-rich-text table {
  overflow: hidden;
  width: 100%;

  td,
  th {
    border: 1px solid var(--tml-table-row-border-color);
    width: 0;
    padding: 6px 8px;
    vertical-align: top;
  }

  th {
    background-color: var(--tml-table-header-background-color);
    color: var(--tml-table-header-text-color);
    border-color: var(--tml-table-header-border-color);
    border-width: var(--tml-table-header-border-width);
    font-weight: bold;
    text-align: left;
  }
}
</style>
